import type { LeagueSquad, SquadPlayer, TransferHistoryEntry } from '../types/database';
import type { PlayerPoolEntry } from '../types/database';
import { calculateSquadPoints } from './pointsCalculation';

/**
 * Pure helpers for the admin injury-replacement flow.
 *
 * Design constraints:
 *   - No Firestore imports — all IO stays in the component.
 *   - Reuses calculateSquadPoints() for points recalculation (single source of truth).
 *   - Does NOT increment any transfer counters (injury = official ruling, not user choice).
 *   - Saves preTransferSnapshot on every update for per-squad rollback via Transfer Management.
 */

export interface InjuryReplacementPreview {
  squad: LeagueSquad;
  userName: string;
  /** The squad player entry being removed. */
  injuredPlayerEntry: SquadPlayer;
  /** Role inherited by the replacement, if any. */
  inheritedRole: 'captain' | 'viceCaptain' | 'xFactor' | null;
  /** Points banked from this squad for the injured player's contribution. */
  bankedDelta: number;
  newBankedTotal: number;
  pointsBefore: number;
  /** Should equal pointsBefore — delta is banked, not gained. */
  pointsAfter: number;
}

function getRoleMultiplier(role: 'captain' | 'viceCaptain' | 'xFactor' | null): number {
  if (role === 'captain') return 2.0;
  if (role === 'viceCaptain') return 1.5;
  if (role === 'xFactor') return 1.25;
  return 1.0;
}

function getPlayerRole(
  squad: LeagueSquad,
  playerId: string,
): 'captain' | 'viceCaptain' | 'xFactor' | null {
  if (squad.captainId === playerId) return 'captain';
  if (squad.viceCaptainId === playerId) return 'viceCaptain';
  if (squad.xFactorId === playerId) return 'xFactor';
  return null;
}

/**
 * Compute the injury replacement update for a single squad.
 *
 * Returns null if:
 *   - The injured player is not in this squad (squad unaffected).
 *   - The replacement player is already in this squad (would create a duplicate).
 */
export function computeInjuryReplacementForSquad(
  squad: LeagueSquad,
  injuredPlayerId: string,
  replacementPoolPlayer: PlayerPoolEntry,
  adminUid: string,
): { updatedSquad: Partial<LeagueSquad>; preview: InjuryReplacementPreview; historyEntry: TransferHistoryEntry } | null {
  // Guard: injured player must be in this squad
  const injuredIndex = squad.players.findIndex((p) => p.playerId === injuredPlayerId);
  if (injuredIndex === -1) return null;

  // Guard: replacement must not already be in this squad
  const replacementAlreadyInSquad = squad.players.some(
    (p) => p.playerId === replacementPoolPlayer.playerId,
  );
  if (replacementAlreadyInSquad) return null;

  const injuredPlayer = squad.players[injuredIndex];
  const inheritedRole = getPlayerRole(squad, injuredPlayerId);
  const roleMultiplier = getRoleMultiplier(inheritedRole);

  // Bank the injured player's earned contribution (signed — handles negative deltas correctly)
  const bankedDelta =
    (injuredPlayer.points - (injuredPlayer.pointsAtJoining ?? 0)) * roleMultiplier;
  const previousBanked = squad.bankedPoints ?? 0;
  const newBankedTotal = previousBanked + bankedDelta;

  // Build replacement SquadPlayer
  const now = new Date();
  const replacementPlayer: SquadPlayer = {
    playerId: replacementPoolPlayer.playerId,
    playerName: replacementPoolPlayer.name,
    team: replacementPoolPlayer.team,
    role: replacementPoolPlayer.role,
    points: replacementPoolPlayer.points,
    matchPerformances: {},
    addedAt: now,
    pointsAtJoining: replacementPoolPlayer.points,
    isOverseas: replacementPoolPlayer.isOverseas,
    ...(replacementPoolPlayer.price !== undefined && { price: replacementPoolPlayer.price }),
    // Role fields set below if inherited
  };

  if (inheritedRole !== null) {
    replacementPlayer.pointsWhenRoleAssigned = replacementPoolPlayer.points;
  }

  // Swap player in array (preserve order)
  const newPlayers = [...squad.players];
  newPlayers[injuredIndex] = replacementPlayer;

  // Update role IDs if the injured player held a role
  let newCaptainId = squad.captainId;
  let newViceCaptainId = squad.viceCaptainId;
  let newXFactorId = squad.xFactorId;
  if (inheritedRole === 'captain') newCaptainId = replacementPoolPlayer.playerId;
  if (inheritedRole === 'viceCaptain') newViceCaptainId = replacementPoolPlayer.playerId;
  if (inheritedRole === 'xFactor') newXFactorId = replacementPoolPlayer.playerId;

  // Recalculate points via shared utility
  const squadSize = squad.players.length > 11 ? 11 : squad.players.length;
  const { totalPoints: pointsAfterLive } = calculateSquadPoints(
    newPlayers,
    squadSize,
    newCaptainId,
    newViceCaptainId,
    newXFactorId,
    newBankedTotal,
  );

  const { totalPoints: pointsBefore } = calculateSquadPoints(
    squad.players,
    squadSize,
    squad.captainId,
    squad.viceCaptainId,
    squad.xFactorId,
    previousBanked,
  );

  // Build history entry
  const historyEntry = buildInjuryReplacementHistoryEntry({
    playerOut: injuredPlayerId,
    playerIn: replacementPoolPlayer.playerId,
    bankedDelta,
    previousBanked,
    pointsBefore,
    pointsAfter: pointsAfterLive,
    inheritedRole,
    actedByAdminUid: adminUid,
    preTransferSnapshot: {
      players: squad.players.map((p) => ({ ...p })),
      captainId: squad.captainId ?? null,
      viceCaptainId: squad.viceCaptainId ?? null,
      xFactorId: squad.xFactorId ?? null,
      bankedPoints: previousBanked,
      totalPoints: squad.totalPoints,
      timestamp: now,
    },
  });

  const updatedSquad: Partial<LeagueSquad> = {
    players: newPlayers,
    captainId: newCaptainId,
    viceCaptainId: newViceCaptainId,
    xFactorId: newXFactorId,
    bankedPoints: newBankedTotal,
    totalPoints: pointsAfterLive,
    transferHistory: [...(squad.transferHistory ?? []), historyEntry],
    // Intentionally NOT incrementing any transfer counters:
    // benchTransfersUsed, flexibleTransfersUsed, midSeasonTransfersUsed, wildcardTransfersUsed
  };

  const preview: InjuryReplacementPreview = {
    squad,
    userName: squad.squadName, // caller should override with real displayName
    injuredPlayerEntry: injuredPlayer,
    inheritedRole,
    bankedDelta,
    newBankedTotal,
    pointsBefore,
    pointsAfter: pointsAfterLive,
  };

  return { updatedSquad, preview, historyEntry };
}

interface BuildHistoryEntryParams {
  playerOut: string;
  playerIn: string;
  bankedDelta: number;
  previousBanked: number;
  pointsBefore: number;
  pointsAfter: number;
  inheritedRole: 'captain' | 'viceCaptain' | 'xFactor' | null;
  actedByAdminUid: string;
  preTransferSnapshot: NonNullable<TransferHistoryEntry['preTransferSnapshot']>;
}

/**
 * Build a TransferHistoryEntry for an injury replacement.
 *
 * Always stamped with transferType: 'injury_replacement' and actedByAdminUid.
 */
export function buildInjuryReplacementHistoryEntry(
  params: BuildHistoryEntryParams,
): TransferHistoryEntry {
  return {
    timestamp: new Date(),
    transferType: 'injury_replacement',
    changeType: 'playerSubstitution',
    playerOut: params.playerOut,
    playerIn: params.playerIn,
    bankedAmount: params.bankedDelta,
    totalBankedAfter: params.previousBanked + params.bankedDelta,
    pointsBefore: params.pointsBefore,
    pointsAfter: params.pointsAfter,
    actedByAdminUid: params.actedByAdminUid,
    preTransferSnapshot: params.preTransferSnapshot,
  };
}
